import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LangSwitch from "@/components/LangSwitch";
import Logo from "@/components/Logo";
import { Eye, EyeSlash, ArrowRight, ArrowLeft, House } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login } = useAuth();
  const { t, isRtl } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const safeNext = nextPath && nextPath.startsWith("/") && !nextPath.startsWith("//") ? nextPath : null;
      navigate(safeNext || "/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(" ") : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(165deg, #070709 0%, #0A0D12 50%, #070709 100%)" }}>
      <button
        onClick={() => navigate("/")}
        data-testid="auth-back-home"
        className={`absolute top-4 ${isRtl ? "right-4" : "left-4"} z-10 inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-slate-300 hover:text-white font-['Outfit'] text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-lg transition-all`}
      >
        <House size={16} weight="regular" />
        <span className="hidden sm:inline">{t('nav_back_home')}</span>
      </button>
      <div className={`absolute top-4 ${isRtl ? "left-4" : "right-4"} z-10`}><LangSwitch /></div>
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-20" style={{ border: "1px solid rgba(1,175,155,0.15)", transform: "translate(30%, -30%)" }} />
      <div className="w-full max-w-md animate-fade-in-up" data-testid="login-page">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <Logo size="xl" />
        </div>
        <div className="bg-[#111216] border border-slate-800/60 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
          <h1 className="font-['Outfit'] text-2xl font-bold text-white mb-2">{t('welcome_back')}</h1>
          <p className="text-slate-400 text-sm mb-6">{t('sign_in_desc')}</p>
          {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-red-400 text-sm" data-testid="login-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1.5 block">{t('email')}</label>
              <input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all"
                placeholder="you@company.com" />
            </div>
            <div>
              <label className="font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1.5 block">{t('password')}</label>
              <div className="relative">
                <input data-testid="login-password" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all pr-10" />
                <button type="button" onClick={() => setShowPw(!showPw)} className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300`}>
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-[#01AF9B] hover:text-[#01AF9B]/80 text-xs font-medium" data-testid="forgot-password-link">{t('login_forgot_password')}</Link>
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><span>{t('sign_in')}</span><ArrowRight size={18} /></>}
            </button>
          </form>
          <p className="text-center text-slate-500 text-sm mt-5">
            {t('no_account')} <Link to={nextPath ? `/register?next=${encodeURIComponent(nextPath)}` : "/register"} className="text-[#01AF9B] hover:text-[#01AF9B]/80 font-medium" data-testid="go-register">{t('create_one')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
